/**
 * @file Context: AudioContext
 * @description Global context for managing audio settings and preferences
 */

import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

interface AudioContextType {
  isMusicEnabled: boolean;
  isSfxEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
  toggleMusic: () => void;
  toggleSfx: () => void;
  setMusicVolume: (volume: number) => void;
  setSfxVolume: (volume: number) => void;
}

const defaultContext: AudioContextType = {
  isMusicEnabled: true,
  isSfxEnabled: true,
  musicVolume: 0.5,
  sfxVolume: 0.5,
  toggleMusic: () => {},
  toggleSfx: () => {},
  setMusicVolume: () => {},
  setSfxVolume: () => {},
};

const AudioContext = createContext<AudioContextType>(defaultContext);

export const useAudioContext = () => useContext(AudioContext);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Load saved preferences from localStorage
  const [isMusicEnabled, setIsMusicEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('clue_hunter_musicEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [isSfxEnabled, setIsSfxEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('clue_hunter_sfxEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [musicVolume, setMusicVolume] = useState<number>(() => {
    const saved = localStorage.getItem('clue_hunter_musicVolume');
    return saved !== null ? JSON.parse(saved) : 0.5;
  });

  const [sfxVolume, setSfxVolume] = useState<number>(() => {
    const saved = localStorage.getItem('clue_hunter_sfxVolume');
    return saved !== null ? JSON.parse(saved) : 0.5;
  });

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem(
      'clue_hunter_musicEnabled',
      JSON.stringify(isMusicEnabled),
    );
  }, [isMusicEnabled]);

  useEffect(() => {
    localStorage.setItem(
      'clue_hunter_sfxEnabled',
      JSON.stringify(isSfxEnabled),
    );
  }, [isSfxEnabled]);

  useEffect(() => {
    localStorage.setItem(
      'clue_hunter_musicVolume',
      JSON.stringify(musicVolume),
    );
  }, [musicVolume]);

  useEffect(() => {
    localStorage.setItem('clue_hunter_sfxVolume', JSON.stringify(sfxVolume));
  }, [sfxVolume]);

  const toggleMusic = () => setIsMusicEnabled((prev) => !prev);
  const toggleSfx = () => setIsSfxEnabled((prev) => !prev);

  const handleSetMusicVolume = (volume: number) => {
    setMusicVolume(Math.max(0, Math.min(1, volume)));
  };

  const handleSetSfxVolume = (volume: number) => {
    setSfxVolume(Math.max(0, Math.min(1, volume)));
  };

  return (
    <AudioContext.Provider
      value={{
        isMusicEnabled,
        isSfxEnabled,
        musicVolume,
        sfxVolume,
        toggleMusic,
        toggleSfx,
        setMusicVolume: handleSetMusicVolume,
        setSfxVolume: handleSetSfxVolume,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};
